import { useState, useCallback, useEffect } from 'react';
import {
  ProposalState,
  User,
  ProfileData,
  FormatType,
  ClientContext,
  ProposalMetadata
} from '../types';
import { proposalPersistenceService } from '../services/proposalPersistenceService';
import { proposalApi } from '../services/proposalApi';
import { useAuth } from '../../../contexts/AuthContext';
import { deriveClientName } from '../utils/clientName';

const initialState: ProposalState = {
  // Team selection
  availableUsers: [],
  selectedUsers: [],

  // Client context
  clientContext: {
    url: '',
    scrapedContent: undefined,
    manualInput: '',
    extractedInfo: undefined
  },

  // Generation
  formatType: 'one-slider',
  generationOptions: {
    format: 'one-slider',
    includePhotos: true
  },
  isGenerating: false,

  // Generated profiles
  generatedProfiles: [],
  editedProfiles: [],
  currentProfileIndex: 0,

  // Export
  exportOptions: {
    format: 'pptx'
  },
  isExporting: false,
  showViewer: false,

  // UI State
  isDirty: false,

  // Proposal metadata
  proposalId: undefined,
  metadata: undefined,

  // Reset counter for forcing component remount
  resetCounter: 0
};

export const useProposalGenerator = () => {
  const [state, setState] = useState<ProposalState>(initialState);
  const [isInitialized, setIsInitialized] = useState(false);
  const { user, getAccessToken } = useAuth();
  const userId = user?.localAccountId || user?.homeAccountId || 'anonymous';

  // Load saved state from IndexedDB on mount
  useEffect(() => {
    const loadPersistedState = async () => {
      try {
        // DISABLED: IndexedDB cache causes sync issues with backend deletions
        // Just start fresh on each page load - backend has its own cache
        /*
        const recentProposal = await proposalPersistenceService.getLatestProposal(userId);

        if (recentProposal) {
          console.log('Loading persisted proposal:', recentProposal.proposalId);

          const friendlyClientName = deriveClientName({
            metadataName: recentProposal.clientName,
            manualInput: recentProposal.manual_context,
            url: recentProposal.clientUrl
          });

          // Reconstruct selected users from saved data
          let selectedUsers: User[] = [];

          // Use teamMemberDetails if available (new format)
          if (recentProposal.teamMemberDetails && recentProposal.teamMemberDetails.length > 0) {
            selectedUsers = recentProposal.teamMemberDetails.map(member => ({
              id: member.id,
              displayName: member.displayName,
              mail: member.mail || '',
              department: member.department || '',
              jobTitle: member.jobTitle || '',
              officeLocation: '',
              hasProfile: true,
              employmentType: 'fullTime' as const
            }));
          }
          // Fallback: try to reconstruct from profiles
          else if (recentProposal.profiles && recentProposal.profiles.length > 0) {
            selectedUsers = recentProposal.profiles.map((profile: any) => ({
              id: profile.user_id || profile.userId || profile.id,
              displayName: profile.display_name || profile.displayName || profile.name || profile.user_id || 'Unknown',
              mail: profile.email || profile.mail || '',
              department: profile.department || '',
              jobTitle: profile.job_title || profile.jobTitle || profile.title || '',
              officeLocation: '',
              hasProfile: true,
              employmentType: 'fullTime' as const
            }));
          }

          setState(prevState => ({
            ...prevState,
            proposalId: recentProposal.proposalId,
            selectedUsers,  // Restore selected users
            clientContext: {
              url: recentProposal.clientUrl || '',
              scrapedContent: '',
              manualInput: recentProposal.manual_context || ''
            },
            generatedProfiles: recentProposal.profiles || [],
            editedProfiles: recentProposal.profiles ? [...recentProposal.profiles] : [],
            formatType: recentProposal.formatType,
            generatedFormatType: recentProposal.formatType, // The loaded profiles were generated with this format
            currentProfileIndex: 0,
            metadata: {
              clientName: friendlyClientName,
              createdAt: recentProposal.createdAt,
              updatedAt: recentProposal.updatedAt,
              createdBy: '',
              teamMembers: recentProposal.teamMembers || [],
              format: recentProposal.formatType
            }
          }));
        }
        */

        // Also check sessionStorage as fallback for temporary state
        const sessionState = sessionStorage.getItem('proposalGeneratorState');
        if (sessionState) {
          try {
            const parsed = JSON.parse(sessionState);
            setState(prevState => ({ ...prevState, ...parsed }));
          } catch (error) {
            console.error('Failed to parse session state:', error);
          }
        }
      } catch (error) {
        console.error('Failed to load persisted state:', error);
      } finally {
        setIsInitialized(true);
      }
    };

    loadPersistedState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]); // getAccessToken is stable, no need to include

  // Auto-save to IndexedDB when state changes (debounced)
  // DISABLED: IndexedDB cache causes sync issues
  /*
  useEffect(() => {
    if (!isInitialized) return;

    const saveTimer = setTimeout(async () => {
      if (state.generatedProfiles.length > 0 && state.proposalId) {
        try {
          const clientName = deriveClientName({
            metadataName: state.metadata?.clientName,
            extractedName: state.clientContext.extractedInfo?.companyName,
            manualInput: state.clientContext.manualInput,
            url: state.clientContext.url
          });

          const proposal = {
            proposalId: state.proposalId,
            clientName,
            clientUrl: state.clientContext.url,
            createdAt: state.metadata?.createdAt || new Date(),
            updatedAt: new Date(),
            createdBy: userId,  // Include user ID
            teamMembers: state.selectedUsers.map(u => u.id),
            profiles: state.editedProfiles.length > 0 ? state.editedProfiles : state.generatedProfiles,
            formatType: state.formatType,
            scraped_content: state.clientContext.scrapedContent,
            manual_context: state.clientContext.manualInput
          };
          
          // Add team member details for proper restoration
          const proposalWithDetails = {
            ...proposal,
            teamMemberDetails: state.selectedUsers.map(u => ({
              id: u.id,
              displayName: u.displayName,
              mail: u.mail,
              department: u.department,
              jobTitle: u.jobTitle
            }))
          };

          await proposalPersistenceService.saveProposal(proposalWithDetails);
          console.log('Auto-saved proposal to IndexedDB');
        } catch (error) {
          console.error('Failed to auto-save proposal:', error);
        }
      }
    }, 1000); // Debounce for 1 second

    return () => clearTimeout(saveTimer);
  }, [state, isInitialized, userId]);
  */

  // Keep sessionStorage as a navigation backup, but DAG-003: strip the
  // governed fields (full profile bodies, scraped client content, manual
  // project context). When the tab is restored, generatedProfiles and
  // editedProfiles must be re-fetched from the backend by proposalId.
  useEffect(() => {
    if (!isInitialized) return;

    const stateToSave = {
      selectedUsers: state.selectedUsers,
      clientContext: {
        url: state.clientContext.url,
        // scrapedContent and manualInput intentionally omitted
        extractedInfo: state.clientContext.extractedInfo,
      },
      formatType: state.formatType,
      currentProfileIndex: state.currentProfileIndex,
      proposalId: state.proposalId,
      metadata: state.metadata,
    };
    sessionStorage.setItem('proposalGeneratorState', JSON.stringify(stateToSave));
  }, [state, isInitialized]);

  // Actions
  const actions = {

    // Team selection
    setAvailableUsers: useCallback((users: User[]) => {
      setState(prev => ({ ...prev, availableUsers: users }));
    }, []),

    setSelectedUsers: useCallback((users: User[]) => {
      setState(prev => ({ ...prev, selectedUsers: users, isDirty: true }));
    }, []),

    // Client context
    setClientContext: useCallback((context: ClientContext) => {
      setState(prev => ({ ...prev, clientContext: context, isDirty: true }));
    }, []),

    // Format and generation
    setFormatType: useCallback((format: FormatType) => {
      setState(prev => ({
        ...prev,
        formatType: format,
        generationOptions: { ...prev.generationOptions, format },
        exportOptions: {
          ...prev.exportOptions,
          format: format === 'full' ? 'pdf' : 'pptx'
        }
      }));
    }, []),

    setGenerating: useCallback((isGenerating: boolean) => {
      setState(prev => ({ ...prev, isGenerating }));
    }, []),


    // Profiles
    setGeneratedProfiles: useCallback((profiles: ProfileData[]) => {
      setState(prev => ({
        ...prev,
        generatedProfiles: profiles,
        editedProfiles: [...profiles], // Create a copy for editing
        generatedFormatType: prev.formatType, // Remember the format used for generation
        isDirty: false
      }));
    }, []),

    updateProfile: useCallback((index: number, profile: ProfileData) => {
      setState(prev => {
        const newEditedProfiles = [...prev.editedProfiles];
        newEditedProfiles[index] = profile;
        return {
          ...prev,
          editedProfiles: newEditedProfiles,
          isDirty: true
        };
      });
    }, []),

    setCurrentProfileIndex: useCallback((index: number) => {
      setState(prev => ({ ...prev, currentProfileIndex: index }));
    }, []),

    // Profile content switching
    switchProfile: useCallback((newIndex: number) => {
      setState(prev => {
        // Save current profile edits if any
        if (prev.editedProfiles[prev.currentProfileIndex]) {
          const updatedProfiles = [...prev.editedProfiles];
          // Mark current profile as dirty if content changed from original
          if (updatedProfiles[prev.currentProfileIndex].content !== 
              updatedProfiles[prev.currentProfileIndex].originalContent) {
            updatedProfiles[prev.currentProfileIndex].isDirty = true;
          }
        }
        
        return {
          ...prev,
          currentProfileIndex: newIndex
        };
      });
    }, []),

    getCurrentProfileContent: useCallback(() => {
      const profile = state.editedProfiles[state.currentProfileIndex] || 
                     state.generatedProfiles[state.currentProfileIndex];
      if (!profile) return '';
      
      // Simply return the current content
      return profile.content || '';
    }, [state]),

    updateCurrentProfileContent: useCallback((content: string) => {
      setState(prev => {
        const updatedProfiles = [...prev.editedProfiles];
        if (updatedProfiles[prev.currentProfileIndex]) {
          updatedProfiles[prev.currentProfileIndex] = {
            ...updatedProfiles[prev.currentProfileIndex],
            content: content,
            isDirty: true
          };
        }
        return {
          ...prev,
          editedProfiles: updatedProfiles,
          isDirty: true
        };
      });
    }, []),

    // Export
    setExporting: useCallback((isExporting: boolean) => {
      setState(prev => ({ ...prev, isExporting }));
    }, []),

    setExportedFile: useCallback((file: Blob | undefined, fileName?: string, formatType?: FormatType) => {
      setState(prev => ({
        ...prev,
        exportedFile: file,
        exportedFileName: fileName,
        exportedFormatType: formatType || prev.formatType // Track the format used for export
      }));
    }, []),

    setShowViewer: useCallback((showViewer: boolean) => {
      setState(prev => ({ ...prev, showViewer }));
    }, []),

    // Metadata
    setProposalId: useCallback((proposalId: string | undefined) => {
      setState(prev => ({ ...prev, proposalId }));
    }, []),

    setMetadata: useCallback((metadata?: ProposalMetadata) => {
      setState(prev => ({ ...prev, metadata }));
    }, []),

    // State management
    setDirty: useCallback((isDirty: boolean) => {
      setState(prev => ({ ...prev, isDirty }));
    }, []),

    setError: useCallback((error: string) => {
      setState(prev => ({ ...prev, error }));
    }, []),

    clearError: useCallback(() => {
      setState(prev => ({ ...prev, error: undefined }));
    }, []),

    // Persistence actions.
    //
    // DAG-005: the local cache used to store full profile bodies,
    // scraped client content, manual context, and full team-member
    // PII. The backend now persists every generated proposal (and
    // enforces ownership on read since Round 1 T1), so the local
    // record is a pointer + display metadata only. Resume flows go
    // through proposalApi.getProposalData.
    saveProposal: useCallback(async () => {
      try {
        const clientName = deriveClientName({
          metadataName: state.metadata?.clientName,
          extractedName: state.clientContext.extractedInfo?.companyName,
          manualInput: state.clientContext.manualInput,
          url: state.clientContext.url
        });

        const profileCount =
          state.editedProfiles.length > 0
            ? state.editedProfiles.length
            : state.generatedProfiles.length;

        const proposalPointer = {
          proposalId: state.proposalId || `proposal_${Date.now()}`,
          clientName,
          clientUrl: state.clientContext.url,
          createdAt: state.metadata?.createdAt || new Date(),
          updatedAt: new Date(),
          createdBy: userId,
          formatType: state.formatType,
          profileCount,
        };

        await proposalPersistenceService.saveProposal(proposalPointer);
        console.log('Proposal pointer saved');
      } catch (error) {
        console.error('Failed to save proposal pointer:', error);
        throw error;
      }
    }, [state, userId]),

    loadProposal: useCallback(async (proposalId: string) => {
      try {
        setState(prev => ({ ...prev, isLoading: true, error: undefined }));

        // IndexedDB disabled - fetch directly from backend.
        // DAG-REVIEW-003 (Round 4 #85): no more console-logging of the
        // server proposal body, reconstructed proposal, clientUrl, or
        // manual_context — those carry governed personal / client data
        // and don't belong in browser console output in production.
        const token = await getAccessToken();
        if (!token) {
          throw new Error('No access token available');
        }

        const serverProposal = await proposalApi.getProposalData(proposalId, token);
        if (!serverProposal) {
          throw new Error('Proposal not found on server');
        }

        // Extract team member details from profiles
        const teamMemberDetails = serverProposal.profiles.map((profile: any) => ({
          id: profile.user_id || profile.userId || profile.id,
          displayName: profile.display_name || profile.displayName || profile.name || profile.user_id || 'Unknown',
          mail: profile.email || profile.mail || '',
          department: profile.department || '',
          jobTitle: profile.job_title || profile.jobTitle || profile.title || ''
        }));

        // Convert server format to local format
        const clientNameFromServer = deriveClientName({
          metadataName: serverProposal.clientName,
          manualInput: serverProposal.manual_context,
          url: serverProposal.clientUrl
        });

        const proposal = {
          proposalId: serverProposal.proposalId,
          clientName: clientNameFromServer,
          clientUrl: serverProposal.clientUrl || '',
          createdAt: new Date(serverProposal.createdAt),
          updatedAt: new Date(serverProposal.updatedAt || serverProposal.createdAt),
          teamMembers: serverProposal.teamMembers || [],
          teamMemberDetails,
          formatType: serverProposal.formatType as FormatType,
          profiles: serverProposal.profiles,
          scraped_content: serverProposal.scraped_content || '',
          manual_context: serverProposal.manual_context || '',
          createdBy: userId
        };

        const friendlyClientName = deriveClientName({
          metadataName: proposal.clientName,
          manualInput: proposal.manual_context,
          url: proposal.clientUrl
        });

        setState(prev => {
          // Reconstruct selected users from saved team member details or profiles
          let selectedUsers: User[] = [];

          // First, try to use teamMemberDetails if available (new format)
          if (proposal.teamMemberDetails && proposal.teamMemberDetails.length > 0) {
            selectedUsers = proposal.teamMemberDetails.map(member => ({
              id: member.id,
              displayName: member.displayName,
              mail: member.mail || '',
              department: member.department || '',
              jobTitle: member.jobTitle || '',
              officeLocation: '',
              hasProfile: true,
              employmentType: 'fullTime' as const
            }));
          }
          // Fallback to old method if teamMemberDetails not available
          else if (proposal.teamMembers && proposal.teamMembers.length > 0) {
            // Try to match team member IDs with available users
            selectedUsers = prev.availableUsers.filter((user: User) =>
              proposal.teamMembers.includes(user.id)
            );

            // If we couldn't find all users in availableUsers, try to reconstruct from profiles
            if (selectedUsers.length < proposal.teamMembers.length) {
              const foundIds = selectedUsers.map(u => u.id);
              const missingIds = proposal.teamMembers.filter(id => !foundIds.includes(id));

              // Try to reconstruct missing users from profile data
              for (const profileData of proposal.profiles || []) {
                const userId = profileData.user_id || profileData.userId || profileData.id;
                if (missingIds.includes(userId)) {
                  const displayName = profileData.display_name || profileData.displayName || profileData.name || userId;
                  // Don't add duplicate
                  if (!selectedUsers.find(u => u.id === userId)) {
                    selectedUsers.push({
                      id: userId,
                      displayName: displayName,
                      mail: profileData.email || profileData.mail || '',
                      department: profileData.department || '',
                      jobTitle: profileData.job_title || profileData.jobTitle || profileData.title || '',
                      officeLocation: '',
                      hasProfile: true,
                      employmentType: 'fullTime' as const
                    });
                  }
                }
              }
            }
          }

          // Ensure profiles have the name field populated
          const profilesWithNames = (proposal.profiles || []).map((profile: any) => {
            // Try to find the corresponding user to get display name
            const user = selectedUsers.find(u =>
              u.id === profile.user_id ||
              u.id === profile.userId ||
              u.id === profile.id
            );

            return {
              ...profile,
              name: profile.name ||
                    profile.display_name ||
                    profile.displayName ||
                    user?.displayName ||
                    profile.user_id ||
                    profile.userId ||
                    'Unknown User'
            };
          });

          const newState = {
            ...prev,
            proposalId: proposal.proposalId,
            selectedUsers,
            clientContext: {
              url: proposal.clientUrl || '',
              scrapedContent: proposal.scraped_content || '',
              manualInput: proposal.manual_context || ''
            },
            generatedProfiles: profilesWithNames,
            editedProfiles: [...profilesWithNames], // Initialize with copy for editing
            formatType: proposal.formatType,
            generatedFormatType: proposal.formatType, // The loaded profiles were generated with this format
            currentProfileIndex: 0,
            metadata: {
              clientName: friendlyClientName,
              createdAt: proposal.createdAt,
              updatedAt: proposal.updatedAt,
              createdBy: proposal.createdBy || '',
              teamMembers: proposal.teamMembers || [],
              format: proposal.formatType
            },
            isLoading: false
          };

          return newState;
        });
      } catch (error) {
        console.error('Failed to load proposal:', error);
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: error instanceof Error ? error.message : 'Failed to load proposal'
        }));
      }
    }, [getAccessToken, userId]),

    // Reset
    reset: useCallback(async () => {
      // Clear the current proposal from IndexedDB first
      if (state.proposalId) {
        try {
          await proposalPersistenceService.deleteProposal(state.proposalId);
        } catch (error) {
          console.error('Failed to clear proposal from IndexedDB:', error);
        }
      }

      // Clear session storage
      sessionStorage.removeItem('proposalGeneratorState');

      // Reset to initial state (create a fresh copy to ensure all references are cleared)
      // Increment resetCounter to force component remount
      setState({
        ...initialState,
        clientContext: {
          url: '',
          scrapedContent: undefined,
          manualInput: '',
          extractedInfo: undefined
        },
        selectedUsers: [],
        generatedProfiles: [],
        editedProfiles: [],
        proposalId: undefined,
        metadata: undefined,
        resetCounter: state.resetCounter + 1  // Increment to force remount
      });
    }, [state.proposalId, state.resetCounter])
  };

  // Simple validation for generation
  const validation = {
    canGenerate: useCallback((): boolean => {
      // Only require at least one selected user - client context is optional
      return state.selectedUsers.length > 0;
    }, [state]),

    canExport: useCallback((): boolean => {
      return state.generatedProfiles.length > 0;
    }, [state])
  };


  return {
    state,
    actions,
    validation
  };
};
